import type { Toast } from '../types/toasts';
import { ref } from 'vue';
import type { RouteLocationNamedRaw } from 'vue-router';

const DEFAULT_TOAST_TIMEOUT = 5000;
const DEFAULT_MAX_TOASTS = 5;
const DEFAULT_GENERIC_ERROR: Omit<Toast, 'uuid' | 'type'> = {
  title: '',
  description: '',
};

let toastTimeout = DEFAULT_TOAST_TIMEOUT;
let maxToasts = DEFAULT_MAX_TOASTS;
let genericError = DEFAULT_GENERIC_ERROR;

export const linkIsRouteLocation = (link: string | RouteLocationNamedRaw | undefined): link is RouteLocationNamedRaw => {
  return !!link && typeof link !== 'string';
};

export const toasts = ref<Toast[]>([]);

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
const toastStartTimes = new Map<string, number>();
const toastOriginalTimeouts = new Map<string, number>();
const pausedToastTimeouts = new Map<string, {
  remainingTime: number;
  originalTimeout: number;
}>();

export function configureToasts(config: {
  toastTimeout?: number;
  maxToasts?: number;
  genericError?: Omit<Toast, 'uuid' | 'type'>;
}) {
  toastTimeout
    = typeof config.toastTimeout === 'number' && config.toastTimeout >= 0
      ? Math.floor(config.toastTimeout)
      : DEFAULT_TOAST_TIMEOUT;
  // Ensure at least one toast is allowed
  maxToasts
    = typeof config.maxToasts === 'number' && config.maxToasts >= 1
      ? Math.floor(config.maxToasts)
      : DEFAULT_MAX_TOASTS;
  // Clone to avoid external mutation affecting internal defaults
  genericError = {
    ...DEFAULT_GENERIC_ERROR,
    ...(config.genericError ?? {}),
  };
}

export function addToast(toast: Omit<Toast, 'uuid'>, timeout: number = toastTimeout) {
  const _toast: Toast = {
    ...toast,
    uuid: crypto.randomUUID(),
  };

  // Ensure we never exceed maxToasts, even if maxToasts was just lowered
  while (toasts.value.length >= maxToasts) {
    removeToast(toasts.value[0]!.uuid);
  }

  toasts.value.push(_toast);

  const startTime = Date.now();
  toastStartTimes.set(_toast.uuid, startTime);
  toastOriginalTimeouts.set(_toast.uuid, timeout);

  const timerId = setTimeout(() => {
    removeToast(_toast.uuid);
  }, timeout);
  toastTimeouts.set(_toast.uuid, timerId);
};

export function removeToast(uuid: string) {
  const timerId = toastTimeouts.get(uuid);
  if (timerId) {
    clearTimeout(timerId);
    toastTimeouts.delete(uuid);
  }

  // Clean up tracking maps
  toastStartTimes.delete(uuid);
  toastOriginalTimeouts.delete(uuid);
  pausedToastTimeouts.delete(uuid);

  const index = toasts.value.findIndex((toast) => toast.uuid === uuid);
  if (index !== -1) {
    toasts.value.splice(index, 1);
  }
}

export function getDefaultErrorToast(): Omit<Toast, 'uuid'> {
  return {
    type: 'error',
    ...genericError,
  };
}

export function addGenericErrorToast() {
  addToast(getDefaultErrorToast());
}

export function pauseToastTimeout(uuid: string) {
  const timerId = toastTimeouts.get(uuid);
  const startTime = toastStartTimes.get(uuid);
  const originalTimeout = toastOriginalTimeouts.get(uuid);

  if (timerId == null || startTime == null || originalTimeout == null) return;

  // Calculate remaining time
  const elapsed = Date.now() - startTime;
  const remainingTime = Math.max(0, originalTimeout - elapsed);

  // Clear the current timeout
  clearTimeout(timerId);
  toastTimeouts.delete(uuid);

  // Store the paused state
  pausedToastTimeouts.set(uuid, {
    remainingTime,
    originalTimeout,
  });
}

export function resumeToastTimeout(uuid: string) {
  const pausedData = pausedToastTimeouts.get(uuid);

  if (!pausedData) return;

  // Resume with remaining time
  const timerId = setTimeout(() => {
    removeToast(uuid);
  }, pausedData.remainingTime);

  // Update tracking - set the remaining time as the new "original" timeout
  toastTimeouts.set(uuid, timerId);
  toastStartTimes.set(uuid, Date.now());
  toastOriginalTimeouts.set(uuid, pausedData.remainingTime);
  pausedToastTimeouts.delete(uuid);
}
